import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type FormGroup } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import {
  NgCommonModule,
  TypedFormGroup,
  validFormGroupChanges$,
  type TypedFormElements,
} from '@principle-theorem/ng-shared';
import {
  QueryScopeConfigId,
  type IQueryScopeRequests,
} from '@principle-theorem/principle-core/interfaces';
import { QueryScopeBuilder } from '@principle-theorem/reporting';
import {
  IBaseMeasures,
  IReportBuilderDataSource,
} from '@principle-theorem/reporting/interfaces';
import {
  asyncForEach,
  ITimePeriod,
  snapshot,
  toTimestamp,
} from '@principle-theorem/shared';
import { combineLatest, noop, ReplaySubject, type Observable } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { QueryScopeForms } from './scope-form';

@Component({
  selector: 'pr-query-scope-form',
  templateUrl: './query-scope-form.component.html',
  styleUrls: ['./query-scope-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'prQueryScopeForm',
  imports: [NgCommonModule, MatFormFieldModule, MatSelectModule],
})
export class QueryScopeFormComponent {
  private _dataSource$ = new ReplaySubject<IReportBuilderDataSource>(1);
  scopeForms$ = new ReplaySubject<QueryScopeForms>(1);
  factTable$: Observable<IBaseMeasures>;
  rootForm = new TypedFormGroup<IQueryScopeRequests>(
    {} as TypedFormElements<IQueryScopeRequests>
  );
  @Output() formChange = new EventEmitter<IQueryScopeRequests>();
  @Output() dateRangeChange = new EventEmitter<ITimePeriod>();

  @Input()
  set scopeForms(scopeForms: QueryScopeForms) {
    if (scopeForms) {
      this.scopeForms$.next(scopeForms);
    }
  }

  @Input()
  set dataSource(dataSource: IReportBuilderDataSource) {
    if (dataSource) {
      this._dataSource$.next(dataSource);
    }
  }

  constructor() {
    this.factTable$ = this._dataSource$.pipe(
      map((dataSource) => dataSource.factTable)
    );

    combineLatest([this.factTable$, this.scopeForms$])
      .pipe(
        concatMap(async ([factTable, scopeForms]) => {
          await this._setupRootForm(factTable, scopeForms);
          await this._setupDefaultValues(scopeForms);
        }),
        takeUntilDestroyed()
      )
      .subscribe(noop);

    validFormGroupChanges$(this.rootForm)
      .pipe(
        map(() => this.rootForm.getRawValue()),
        takeUntilDestroyed()
      )
      .subscribe((formValue) => this.formChange.emit(formValue));
  }

  setDateRange(dateRange: ITimePeriod, dateRangeForm: FormGroup): void {
    const from = toTimestamp(dateRange.from);
    const to = toTimestamp(dateRange.to);
    dateRangeForm.patchValue({ from, to });
    this.dateRangeChange.emit(dateRange);
  }

  private async _setupRootForm(
    factTable: IBaseMeasures,
    scopeForms: QueryScopeForms
  ): Promise<void> {
    const builder = new QueryScopeBuilder(factTable);
    Object.keys(this.rootForm.controls).map((key) => {
      if (!builder.hasScope(key)) {
        this.rootForm.removeControl(key);
      }
    });

    await asyncForEach(factTable.scopes, async (scope) => {
      const scopeForm = scopeForms[scope.id as QueryScopeConfigId];
      if (!scopeForm) {
        return;
      }

      if (await snapshot(scopeForm.isShown$)) {
        this.rootForm.addControl(scope.id, scopeForm.formGroup);
      }
    });
  }

  private async _setupDefaultValues(
    scopeForms: QueryScopeForms
  ): Promise<void> {
    if (await snapshot(scopeForms.brand.isShown$)) {
      scopeForms.brand.formGroup.patchValue(
        await scopeForms.brand.getDefaultValue()
      );
    }
    if (await snapshot(scopeForms.practice.isShown$)) {
      scopeForms.practice.formGroup.patchValue(
        await scopeForms.practice.getDefaultValue()
      );
    }
  }
}
